import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useApp } from '@/hooks/use-app-context';
import { Icon } from '@/lib/icons';
import { buildRouteRegistry, filterRoutesForRole, findSimilarRoutes, openGlobalSearch, searchCopy } from '@/features/search';

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const { t, user, isAuthenticated } = useApp();
  const copy = searchCopy[lang === 'en' ? 'en' : 'fr'];

  useEffect(() => {
    console.error('404 Error: User attempted to access non-existent route:', location.pathname);
  }, [location.pathname]);

  const role = isAuthenticated ? user?.role ?? 'user' : 'guest';
  const routes = useMemo(
    () => filterRoutesForRole(buildRouteRegistry(lang ?? 'fr', t), role),
    [lang, role, t],
  );
  const suggestions = useMemo(() => findSimilarRoutes(location.pathname, routes), [location.pathname, routes]);

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-4 pb-12 pt-3 sm:px-6">
      <header className="relative py-8 text-center">
        <p className="flex items-center justify-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.28em] text-brand">
          404
        </p>
        <h1 className="mt-4 text-4xl font-semibold tracking-[-0.07em] text-foreground">
          {t.notFound.title}
        </h1>
      </header>

      {/* Current path */}
      <section className="py-5 text-center">
        <p className="flex items-center justify-center gap-3 text-[0.66rem] font-semibold uppercase tracking-[0.25em] text-slate-500">
          {t.notFound.pathLabel}
        </p>
        <code className="mt-3 block font-mono text-sm text-brand">{location.pathname}</code>
      </section>

      {/* Fuzzy suggestions */}
      {suggestions.length > 0 && (
        <section className="py-5">
          <p className="flex items-center gap-3 text-[0.66rem] font-semibold uppercase tracking-[0.25em] text-slate-500">
            {copy.suggestedRoutes}
          </p>
          <div className="mt-4">
            {suggestions.map((route) => (
              <a
                key={route.href}
                href={route.href}
                className="group flex items-center gap-4 border-b border-border/40 py-4 last:border-b-0 transition-colors hover:text-foreground"
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-border/40 text-slate-400 group-hover:border-brand/25 group-hover:text-brand dark:text-brand transition-colors">
                  <Icon name={route.icon} className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-slate-600 dark:text-slate-300/[0.76] group-hover:text-slate-900 dark:group-hover:text-slate-100">{route.title}</span>
                  {route.description ? (
                    <span className="mt-0.5 block truncate text-xs text-slate-500">{route.description}</span>
                  ) : null}
                </span>
                <Icon name="arrowRight" className="ml-auto h-4 w-4 text-foreground/14 group-hover:text-brand dark:text-brand transition-colors" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Actions */}
      <div className="flex justify-center gap-3">
        <button
          type="button"
          onClick={() => openGlobalSearch()}
          className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand px-5 py-2.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-brand"
        >
          <Icon name="search" className="h-4 w-4" />
          {copy.openSearch}
          <kbd className="ml-1 rounded-full border border-slate-950/10 bg-slate-950/5 px-1.5 py-0.5 text-[0.62rem] font-semibold tracking-[0.1em]">
            {copy.shortcut}
          </kbd>
        </button>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-full border border-border/40 bg-transparent px-5 py-2.5 text-sm text-slate-400 transition-colors hover:bg-slate-900/[0.03] dark:hover:bg-white/[0.03] hover:text-foreground"
        >
          {t.notFound.previousPage}
        </button>
      </div>

      {/* Did you know */}
      <section className="pt-5 text-center">
        <p className="text-xs leading-6 text-slate-500">
          <span className="mr-2 font-semibold text-brand">{t.notFound.didYouKnow}</span>
          {copy.didYouKnow}
        </p>
      </section>
    </div>
  );
};

export default NotFound;
